import { env } from "config/Env";
import { SignJWT, importPKCS8 } from "jose";

// Variable en memoria para cachear la CryptoKey importada y optimizar rendimiento
let cachedPrivateKey = null;

/**
 * Convierte la llave privada PKCS#8 en formato PEM a un objeto CryptoKey
 * compatible con la API de WebCrypto del navegador.
 * 
 * @returns {Promise<CryptoKey>} Llave privada procesada
 */
async function getPrivateKey() {
  if (cachedPrivateKey) {
    return cachedPrivateKey;
  }

  if (!env.MASTER_SA_PRIVATE_KEY) {
    throw new Error(
      "❌ [RSA SIGNER ERROR]: La llave privada RS256 de la Service Account no está configurada en REACT_APP_MASTER_SA_PRIVATE_KEY."
    );
  }

  try {
    cachedPrivateKey = await importPKCS8(env.MASTER_SA_PRIVATE_KEY, "RS256");
    return cachedPrivateKey;
  } catch (error) {
    console.error("❌ [RSA SIGNER ERROR]: Fallo al importar la llave privada PKCS#8:", error);
    throw new Error("Formato de llave privada RS256 inválido o corrupto.");
  }
}

/**
 * Genera un token JWT firmado mediante algoritmo RS256 para autenticación M2M
 * ante el backend de Firebase Cloud Functions.
 * 
 * @param {Array<string>} [customScopes] Scopes opcionales para acotar el token
 * @returns {Promise<string>} JWT firmado en formato compacto (header.payload.signature)
 */
export async function generateMasterM2MToken(customScopes = null) {
  try {
    const privateKey = await getPrivateKey();
    const now = Math.floor(Date.now() / 1000);
    const scopesToApply = customScopes || env.MASTER_SA_SCOPES;

    const jwt = await new SignJWT({
      iss: env.MASTER_SA_CLIENT_ID, // Mapea a 'client_id' de la Service Account en Firestore
      sub: env.MASTER_SA_CLIENT_ID,
      scopes: scopesToApply,
    })
      .setProtectedHeader({
        alg: "RS256",
        kid: env.MASTER_SA_KEY_ID, // Mapea al 'private_key_id'
        typ: "JWT",
      })
      .setIssuedAt(now)
      .setExpirationTime(now + 3600) // Válido por 1 hora (3600s)
      .sign(privateKey);

    return jwt;
  } catch (error) {
    console.error("❌ [RSA SIGNER ERROR]: Fallo al firmar el JWT M2M:", error);
    throw error;
  }
}

/**
 * Limpia la caché de la llave privada (Util para pruebas o rotación de credenciales)
 */
export function clearPrivateKeyCache() {
  cachedPrivateKey = null;
}